import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import AddToast from "../../../state/actions/AddToast";
import { Loading } from "../../../components/atoms";
import { setUser } from "../../../state/actions/userActions";
import {
  getUser,
  saveClinvarToken,
  updateClinvarToken,
} from "../services/clinvar-api";
import HelperText from "../../../components/atoms/HelperText";
import ClinvarTokenHelp from "./ClinvarTokenHelp";
import {
  FormGroup,
  Input,
  InputHelperText,
  Label,
} from "../../../components/atoms/Form";

const ClinvarToken = (props) => {
  const {
    clinvarTokenHashed,
    clinvarApiKey,
    closeTokenModalOnSuccess,
    SetUser,
    userAuthError,
  } = props;
  const [inputToken, setInputToken] = useState();
  const [validationError, setValidationError] = useState("");
  const [tokenError, setTokenError] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (clinvarApiKey) {
      setInputToken(clinvarApiKey);
    } else {
      setInputToken("");
    }
  }, [clinvarApiKey]);

  const updateInputToken = (e) => {
    setInputToken(e.target.value);
  };

  const validateInputToken = (token) => {
    if (token.length > 64) {
      setValidationError("The ClinVar API Key provided is too long.");
      return false;
    }
    if (token.length < 64 && validationError) {
      setValidationError("");
      return true;
    }
    return true;
  };

  const saveToken = () => {
    if (!inputToken || !validateInputToken(inputToken)) return;
    setIsLoading(true);
    const result = clinvarApiKey
      ? updateClinvarToken(inputToken, clinvarTokenHashed)
      : saveClinvarToken(inputToken);
    result
      .then(() => {
        AddToast("Token successfully saved.", "success");
        setInputToken(inputToken);
        // Update user object to get new clinvar_submission_api_key, clinvar_submission_token_id_hashed
        const userRequest = getUser();
        userRequest
          .then((r) => {
            const user = r.data;
            SetUser(user);
            setIsLoading(false);
            if (closeTokenModalOnSuccess) {
              closeTokenModalOnSuccess();
            }
          })
          .catch((err) => {
            setIsLoading(false);
            console.error("Error while getting user:", err);
          });
      })
      .catch((err) => {
        setIsLoading(false);
        const errorMessage = !clinvarApiKey
          ? `Error while saving ClinVar API Key`
          : `Error while updating ClinVar API Key`;
        console.error(`${errorMessage}: ${err.message}`);
        setTokenError(`${errorMessage}: ${err.message}`);
      });
  };

  if (inputToken !== undefined) {
    if (userAuthError) {
      return (
        <div className="tw-px-2 tw-py-4">
          <div className="tw-p-4 tw-text-red-700">
            <b>{userAuthError}</b>
          </div>
        </div>
      );
    }
    return (
      <div className="tw-container tw-p-6">
        <div className="tw-text-2xl tw-mb-2">
          {clinvarApiKey
            ? `Update ClinVar API key`
            : `Configure your ClinVar API Key`}
        </div>
        <div className="tw-text-sm">
          To submit to ClinVar on your behalf, we must have your ClinVar API
          Key. This is a 64 alphanumeric characters, for example
          abcdefgxyzABCDGHI01234..., which uniquely identifies your
          organization. All users who submit for your organization may use the
          same ClinVar API Key. We securely store your ClinVar API Key and use
          it with all your submissions. If your organization does not already
          have one, you must first set up a ClinVar Service Account. Read more
          about the ClinVar API Key here
          <HelperText RenderContent={() => <ClinvarTokenHelp />} />
        </div>
        <div className="tw-pt-6">
          <Label htmlFor="clinvartoken" text="ClinVar API Key" />
          <FormGroup>
            <Input
              type="text"
              id="ClinvarToken"
              required="required"
              value={inputToken}
              onChange={(e) => updateInputToken(e)}
            />
          </FormGroup>
          {validationError && (
            <InputHelperText type="error">{validationError}</InputHelperText>
          )}
        </div>
        {tokenError ? (
          <div className="tw-pt-2 tw-text-red-700">
            <b>{tokenError}</b>
          </div>
        ) : null}
        <div className="tw-flex tw-justify-end tw-pt-8">
          <button
            type="button"
            disabled={!inputToken || isLoading}
            onClick={saveToken}
            className="btn btn-success"
          >
            {isLoading ? <Loading /> : (!clinvarApiKey && `Save`) || `Update`}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

ClinvarToken.propTypes = {
  closeTokenModalOnSuccess: PropTypes.func,
  SetUser: PropTypes.func.isRequired,
  clinvarTokenHashed: PropTypes.string,
  clinvarApiKey: PropTypes.string,
  userAuthError: PropTypes.string,
};

ClinvarToken.defaultProps = {
  closeTokenModalOnSuccess: undefined,
  clinvarApiKey: "",
  clinvarTokenHashed: "",
  userAuthError: "",
};

const mapDispatchToProps = (dispatch) => {
  return {
    SetUser: (user) => dispatch(setUser(user)),
  };
};

export default connect(null, mapDispatchToProps)(ClinvarToken);
